'use client';

import {
  BookOpenIcon,
  BrainIcon,
  CaretDownIcon,
  GearSixIcon,
  HeartIcon,
  SignOutIcon,
  UserIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState, useCallback } from 'react';

import { Badge, MENU_ROW_CLASS, MENU_SURFACE_CLASS } from '../../lib/header-constants';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext, type NavbarUser } from './context';

interface HeaderUserMenuProps {
  user: NavbarUser;
}

const ICON_MAP: Record<string, PhosphorIcon> = {
  book: BookOpenIcon,
  brain: BrainIcon,
  heart: HeartIcon,
  gear: GearSixIcon,
  user: UserIcon,
};

const HeaderUserMenu = ({ user }: HeaderUserMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const { accountLinks, favoritesUrl, onLogout } = useNavbarContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const menuLinks = favoritesUrl
    ? accountLinks.filter((link) => link.href !== favoritesUrl)
    : accountLinks;

  const handleAvatarError = useCallback(() => setAvatarError(true), []);

  useEffect(() => {
    setAvatarError(false);
  }, [user.avatarUrl]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    if (onLogout) await onLogout();
  };

  const firstName = user.name.split(' ')[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'rounded-adipa-lg text-adipa-text-primary flex items-center gap-2 px-2.5 py-1.5 text-sm font-semibold',
          'duration-adipa-fast transition-colors ease-out motion-reduce:transition-none',
          'hover:bg-adipa-surface-lavender active:bg-adipa-surface-lavender',
          'focus-visible:outline-adipa-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span
          aria-hidden="true"
          className="rounded-adipa-full border-adipa-border shadow-adipa-card flex h-7 w-7 shrink-0 items-center justify-center border bg-white p-0.5"
        >
          {user.avatarUrl && !avatarError ? (
            <Image
              alt=""
              className="rounded-adipa-full h-full w-full object-cover"
              height={24}
              src={user.avatarUrl}
              unoptimized
              width={24}
              onError={handleAvatarError}
            />
          ) : (
            <span className="rounded-adipa-full bg-adipa-surface-muted text-adipa-primary flex h-full w-full items-center justify-center">
              <UserIcon size={15} weight="regular" />
            </span>
          )}
        </span>
        <span className="max-w-28 truncate">{firstName}</span>
        <CaretDownIcon
          aria-hidden="true"
          className={cn(
            'duration-adipa-fast shrink-0 transition-transform ease-out',
            isOpen && 'rotate-180',
          )}
          size={14}
          weight="bold"
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className={cn(
              'absolute right-0 top-full z-50 mt-2 w-52 origin-top-right',
              MENU_SURFACE_CLASS,
            )}
            exit={{ opacity: 0, scale: 0.97, y: -6 }}
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            role="menu"
            transition={{ bounce: 0, duration: 0.2, type: 'spring' }}
          >
            {menuLinks.map(({ badge, badgeColor, href, icon, label }) => {
              const Icon = icon ? ICON_MAP[icon] : null;
              return (
                <Link
                  className={cn(MENU_ROW_CLASS, 'flex items-center gap-2.5 whitespace-nowrap')}
                  href={href}
                  key={href}
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  {Icon && <Icon aria-hidden="true" size={16} weight="regular" />}
                  {label}
                  {badge ? <Badge color={badgeColor ?? 'magenta'} label={badge} /> : null}
                </Link>
              );
            })}
            <div aria-hidden="true" className="border-adipa-border my-1 border-t" />
            <button
              className={cn(
                MENU_ROW_CLASS,
                'text-adipa-error hover:bg-adipa-error-surface hover:text-adipa-error flex items-center gap-2.5',
              )}
              role="menuitem"
              type="button"
              onClick={handleLogout}
            >
              <SignOutIcon aria-hidden="true" size={16} weight="regular" />
              Cerrar sesión
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeaderUserMenu;
